import globalLogger from "@lazarentertainment/js-logger"
import {
  Channel,
  OnChannelUpdatedDocument,
  ScheduledShow,
  useGetChannelQuery,
  useGetRecordingQuery,
} from "client/graphql"
import ErrorComponent from "next/error"
import Head from "next/head"
import { createContext, FC, ReactNode, useContext, useEffect } from "react"
import ResponsiveLoader from "shared/components/ResponsiveLoader"
import { AspectRatio } from "shared/constants/views"

export type ChannelContextRecordingState = {
  displayUntrimmedVod: boolean
  vodHlsUrl: string
  startTime: string
  endTime: string
  trimStartTime: number
  trimEndTime: number
  title: string | null
  description: string | null
}

export type ChannelContextState = Omit<Channel, "id"> & {
  channelId: string
  broadcastId: string
  title: string
  broadcastDescription: string
  live: boolean
  aspectRatio: AspectRatio
  watermarkOnPlayer: boolean
  recordingId: string | null
  recording?: ChannelContextRecordingState
  scheduledShows: ScheduledShow[]
  liveSubtitles: boolean
  displayName?: string | null
  username?: string
}

const ChannelContext = createContext<ChannelContextState | undefined>(
  undefined,
)

interface Props {
  channelId: string
  broadcastId?: string | null
  recordingId?: string | null
  assetId?: string | null
  initialChannel?: Channel
  children?: ReactNode
  onLoading?: () => JSX.Element
  onError?: () => JSX.Element
}

const ChannelProvider: FC<Props> = ({
  channelId,
  recordingId,
  assetId,
  onLoading,
  onError,
  children,
}) => {
  const { data, loading, error, subscribeToMore } = useGetChannelQuery({
    variables: { channelId },
  })

  const { data: recordingData, error: recordingError } =
    useGetRecordingQuery({
      variables: { channelId, recordingId },
      skip: !recordingId,
    })
  const recording = recordingData?.recording

  useEffect(() => {
    // if a recordingId is present, do not subscribe to channel updates.
    if (!recordingId) {
      subscribeToMore({
        document: OnChannelUpdatedDocument,
        variables: { channelId },
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData.data) return prev
          const newChannel = (subscriptionData.data as any).channelUpdated

          return { channel: newChannel }
        },
      })
    }
  }, [])

  if (
    error ||
    recordingError ||
    (recording && assetId && assetId !== recording.untrimmedVodVersion) // Wrong assetId, 404
  ) {
    globalLogger.error(error)
    return onError ? (
      onError()
    ) : (
      <ErrorComponent statusCode={404} title={error.message} />
    )
  }

  if (loading || !data) {
    return onLoading ? onLoading() : <ResponsiveLoader />
  }

  const { channel } = data

  if (channel?.reconnect) {
    window.location.reload()
  }

  if (
    channel === undefined ||
    channel === null ||
    recordingError ||
    (recording && assetId && assetId !== recording.untrimmedVodVersion) // Wrong assetId, 404
  ) {
    globalLogger.error(recordingError)
    return onError ? (
      onError()
    ) : (
      <ErrorComponent statusCode={404} title={recordingError.message} />
    )
  }

  // For recordings, 'live' is always true.
  // For channel, 'live' is null if not live, so change to boolean
  const live = recordingId ? true : !!channel.live
  let aspectRatio: AspectRatio = AspectRatio.HORIZONTAL

  if (recording?.aspectRatio) {
    aspectRatio = recording.aspectRatio as AspectRatio
  } else if (channel?.aspectRatio) {
    aspectRatio = channel.aspectRatio as AspectRatio
  }

  const displayUntrimmedVod =
    assetId && assetId === recording?.untrimmedVodVersion

  const vodHlsUrl = displayUntrimmedVod
    ? recording.untrimmedVodHlsUrl
    : recording?.vodHlsUrl

  const channelValues: ChannelContextState = {
    ...channel,
    title: channel.title ?? "",
    broadcastDescription: channel.broadcastDescription ?? "",
    channelId,
    broadcastId: channel.broadcastId,
    live,
    aspectRatio,
    watermarkOnPlayer: channel.watermarkOnPlayer,
    recordingId,
    recording: recording
      ? {
          vodHlsUrl,
          displayUntrimmedVod,
          startTime: recording.startTime,
          endTime: recording.endTime,
          trimStartTime: recording.trimStartTime,
          trimEndTime: recording.trimEndTime,
          title: recording.title,
          description: recording.description,
        }
      : undefined,
    liveSubtitles: !!channel.liveSubtitles,
    username: channel.user.username,
    displayName: channel.user.displayName,
    scheduledShows: channel.scheduledShows ?? [],
  }

  return (
    <>
      <Head>
        <title>{channel.title}</title>
      </Head>
      <ChannelContext.Provider value={channelValues}>
        {children}
      </ChannelContext.Provider>
    </>
  )
}

const useChannelContext = () => {
  const channelContext = useContext(ChannelContext)
  if (!channelContext) {
    throw new Error(
      "No ChannelContext.Provider found when calling useChannelContext.",
    )
  }
  return channelContext
}

export { ChannelContext, ChannelProvider, useChannelContext }
